import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, CalendarDays, Clock, Loader2, UserCheck, AlertCircle } from "lucide-react";

const statusStyle = { CONFIRMED: "status-confirmed", PENDING_CASH: "status-pending", PENDING_PAYMENT: "status-pending", CANCELLED: "status-cancelled" };

export default function CondoDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/condo/dashboard").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load dashboard</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="condo-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <Building2 className="w-7 h-7 text-court-orange" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">{data.condo_name || "Condo Admin"}</h1>
      </div>
      <p className="text-muted-foreground mb-8">Manage residents and facility access</p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Residents", value: data.total_residents, icon: UserCheck, color: "text-lime-volt", click: () => navigate("/condo/residents") },
          { label: "Pending", value: data.pending_requests, icon: AlertCircle, color: "text-amber-500", click: () => navigate("/condo/residents") },
          { label: "Venues", value: data.venues_count, icon: Building2, color: "text-blue-500", click: () => navigate("/condo/rules") },
          { label: "Bookings", value: data.total_bookings, icon: CalendarDays, color: "text-court-orange", click: () => navigate("/condo/reports") },
        ].map((s, i) => (
          <div key={i} onClick={s.click} data-testid={`condo-stat-${i}`}
            className="bg-white border-2 border-slate-100 p-5 cursor-pointer hover:border-slate-200 hover:-translate-y-0.5 transition-all">
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="font-heading text-3xl font-bold">{s.value}</p>
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Bookings */}
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-court-orange" />Recent Bookings
      </h2>
      {data.recent_bookings?.length === 0 ? (
        <p className="text-muted-foreground text-center py-8 border-2 border-dashed border-slate-200">No bookings yet</p>
      ) : (
        <div className="space-y-2">
          {data.recent_bookings?.map(b => (
            <div key={b.booking_id} className="bg-white border border-slate-100 p-3 flex items-center gap-4 text-sm">
              <div className="flex-1">
                <p className="font-medium">{b.user_name || b.user_email}</p>
                <p className="text-xs text-muted-foreground">{b.resource_name} at {b.venue_name}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{b.start_dt?.slice(0, 10)} {b.start_dt?.slice(11, 16)}</p>
                <Badge className={`${statusStyle[b.status]} text-[10px] font-heading uppercase`}>{b.status?.replace("_"," ")}</Badge>
              </div>
              <p className="font-heading font-bold text-court-orange">₱{b.total_amount?.toFixed(0)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
